// alterSchema — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-schema' })` op (DSE2 §3.D6).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D5 — Schema UI.
//
// Per-engine surface (per DSE2 dialect.schema):
//   - pgsql:  ALTER SCHEMA name RENAME TO new
//   - mssql:  unsupported — T-SQL ALTER SCHEMA is reserved for object transfer
//             (covered by Slice D1's `move-table-schema`). The redirect
//             tooltip points at "create new, transfer all, drop old".
//   - mysql:  unsupported — schemas == databases (use slice G1).
//   - oracle: unsupported — schemas == users (use slice F1).
//   - sqlite: unsupported — no DDL schemas.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, SchemaChange } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

import { getCreateSchemaUnsupportedTooltip } from './createSchema';

export interface AlterSchemaArgs {
  readonly schemaName: string;
  readonly quoted?: boolean;
  readonly change: SchemaChange;
}

export function buildAlterSchemaEdit(args: AlterSchemaArgs): DdlEdit {
  return {
    op: 'alter-schema',
    schemaName: args.schemaName,
    ...(args.quoted ? { quoted: true } : {}),
    change: args.change,
  };
}

export async function commitAlterSchema(
  ctx: VqeDdlEditContext,
  args: AlterSchemaArgs,
): Promise<void> {
  const edit = buildAlterSchemaEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_SCHEMA_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'postgres',
] as const);

export function isAlterSchemaSupported(engine: SqlEngine): boolean {
  return ALTER_SCHEMA_SUPPORTED.has(engine);
}

export function getAlterSchemaUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'SQL Server ALTER SCHEMA is reserved for object transfer (use the '
      + 'table’s “Move to schema” action). T-SQL has no schema rename; '
      + 'create the new schema, transfer all objects, drop the old schema.'
    );
  }
  // mysql / oracle / sqlite: same redirect as CREATE.
  return getCreateSchemaUnsupportedTooltip(engine);
}

export function validateAlterSchemaForEngine(
  engine: SqlEngine,
  args: AlterSchemaArgs,
): string | null {
  if (!isAlterSchemaSupported(engine)) {
    return getAlterSchemaUnsupportedTooltip(engine);
  }
  if (args.schemaName.trim().length === 0) {
    return 'Existing schema name is required.';
  }
  if (args.change.renameTo.trim().length === 0) {
    return 'New schema name is required.';
  }
  if (args.change.renameTo.trim() === args.schemaName.trim()) {
    return 'New schema name must differ from the existing name.';
  }
  return null;
}
